import { ref, computed, type ComputedRef } from 'vue'
import { AgentActionsService } from '@/types/generated'
import type { TAgentAction } from '@/types/generated'

export type WidgetStep =
  | 'summary'
  | 'loading'
  | 'review'
  | 'success'
  | 'error'
  | 'feedback'
  | 'feedback-success'

export interface ActionSummaryParts {
  /** e.g. template name, document name — whatever the action's primary subject is */
  label: string
  /** Human-readable attachment/detail names, already formatted per type */
  details: string[]
}

export interface ActionFlowStrategy {
  /** Resolve the payload into display parts for the summary step */
  resolveSummary: (action: TAgentAction) => Promise<ActionSummaryParts | null>
  /** Called when user clicks "Review" — e.g. pre-fill form, scroll to section */
  onReview: (action: TAgentAction) => Promise<void> | void
  /** Called when user clicks "Send"/"Confirm" — execute the action */
  onSend: (action: TAgentAction) => Promise<void>
  /** If true, the backend already commits the action during onSend — skip the explicit commit call */
  backendCommits?: boolean
}

export interface AiAssistantWidgetOptions {
  /** Reactive case UUID — widget fetches actions for this case */
  caseUuid: ComputedRef<string | undefined>
  /** Reactive enabled flag — widget only fetches when true */
  enabled: ComputedRef<boolean>
  /** Optional: called with all fetched actions (e.g. to init ratings) */
  onActionsFetched?: (actions: TAgentAction[]) => void
  /**
   * Called after the user reaches the in-widget review step (e.g. email section prepared).
   * Use for client-only analytics — not invoked for flows that skip the review step.
   */
  onReviewScreenEntered?: (action: TAgentAction | null) => void
}

/**
 * Orchestrates the AI Assistant floating widget.
 *
 * Owns:
 * - Fetching pending actions for a case
 * - A queue of pending actions with automatic advancement
 * - Per-action-type flow strategies (summary resolution, review, send)
 * - Widget state machine: summary → loading → review → send → success → (next action or close)
 * - Commit / discard / rate API calls
 *
 * The page registers one strategy per action type via `registerActionFlow()`.
 * Adding support for a new action type = registering one more flow.
 */
export function useAiAssistantWidget(options: AiAssistantWidgetOptions) {
  const { caseUuid, enabled, onActionsFetched, onReviewScreenEntered } = options

  const isOpen = ref(false)
  const step = ref<WidgetStep>('summary')
  const isAiPreloaded = ref(false)
  const isEdited = ref(false)
  const discardMode = ref(false)

  const pendingActions = ref<TAgentAction[]>([])
  const currentAction = ref<TAgentAction | null>(null)
  const summaryParts = ref<ActionSummaryParts | null>(null)

  const hasPendingActions = computed(() => pendingActions.value.length > 0)

  const flowStrategies = new Map<string, ActionFlowStrategy>()

  const registerActionFlow = (actionType: string, strategy: ActionFlowStrategy) => {
    flowStrategies.set(actionType, strategy)
  }

  const getStrategy = (action: TAgentAction | null): ActionFlowStrategy | undefined => {
    if (!action?.action_type) return undefined
    return flowStrategies.get(action.action_type)
  }

  /** Whether user has edited the AI-prefilled content */
  const draftEdited = computed(() => isEdited.value)

  /** Call after AI pre-fill completes — resets the edited flag */
  const markReviewed = () => {
    isEdited.value = false
  }

  /** Call whenever the user modifies AI-prefilled content */
  const markEdited = () => {
    isEdited.value = true
  }

  const fetchActions = async () => {
    if (!caseUuid.value || !enabled.value) return

    try {
      const response = await AgentActionsService.agentActionsList({
        caseUuid: caseUuid.value,
        ordering: '-created_at'
      })

      if (response.results) {
        onActionsFetched?.(response.results)
      }

      // Filter to pending (not committed, not reverted, not rejected) actions that have a registered flow
      const pending = (response.results ?? []).filter(
        (a) =>
          !a.is_committed &&
          !a.is_reverted &&
          !a.was_rejected_through_soft_delete &&
          a.payload &&
          flowStrategies.has(a.action_type)
      )
      pendingActions.value = pending

      // If we have no current action (or current was completed/discarded), advance to next
      if (
        !currentAction.value ||
        currentAction.value.is_committed ||
        currentAction.value.is_reverted
      ) {
        await advanceToNextAction()
      }
    } catch (error) {
      console.error('Error fetching AI actions:', error)
    }
  }

  const advanceToNextAction = async () => {
    const next = pendingActions.value[0] ?? null
    currentAction.value = next
    summaryParts.value = null
    isEdited.value = false

    if (next) {
      // Resolve summary in background — computed will react when parts.value updates
      const strategy = getStrategy(next)
      if (strategy) {
        try {
          summaryParts.value = await strategy.resolveSummary(next)
        } catch {
          // Non-critical — summary will show fallback text
        }
      }
    }
  }

  const toggle = () => {
    if (!isOpen.value && step.value !== 'review') {
      step.value = 'summary'
    }
    isOpen.value = !isOpen.value
  }

  const close = () => {
    isOpen.value = false
  }

  /** "Review" — loading → strategy.onReview → review step */
  const handleReview = async () => {
    const action = currentAction.value
    const strategy = getStrategy(action)
    if (!action || !strategy) return

    step.value = 'loading'
    try {
      await strategy.onReview(action)
      await new Promise((resolve) => setTimeout(resolve, 400))
      step.value = 'review'
      onReviewScreenEntered?.(action)
    } catch {
      step.value = 'error'
    }
  }

  /** "Send" — loading (min 500ms) → strategy.onSend → commit → success → advance */
  const handleSend = async () => {
    const action = currentAction.value
    const strategy = getStrategy(action)
    if (!action || !strategy) return

    step.value = 'loading'
    const startTime = Date.now()

    try {
      await strategy.onSend(action)

      // Commit the agent action unless the backend already did it during onSend.
      if (action.uuid && !strategy.backendCommits) {
        await AgentActionsService.agentActionsCommitCreate({
          requestBody: { agent_action_uuid: action.uuid }
        })
      }

      // Enforce minimum 500ms loading
      const elapsed = Date.now() - startTime
      if (elapsed < 500) {
        await new Promise((resolve) => setTimeout(resolve, 500 - elapsed))
      }

      step.value = 'success'
    } catch (error) {
      console.error('Error in AI assistant send flow:', error)
      step.value = 'error'
    }
  }

  /** Called after the success step — advance to next pending action or close */
  const handleSuccessDismiss = async () => {
    discardMode.value = false

    // Remove completed action from queue
    if (currentAction.value) {
      pendingActions.value = pendingActions.value.filter(
        (a) => a.uuid !== currentAction.value?.uuid
      )
    }

    // Re-fetch to pick up any new actions that arrived while reviewing
    await fetchActions()

    if (pendingActions.value.length > 0) {
      // More actions — advance and show next summary
      await advanceToNextAction()
      step.value = 'summary'
    } else {
      // All done — close
      currentAction.value = null
      summaryParts.value = null
      close()
    }
  }

  /** "Discard" — reject action, then show feedback step */
  const handleDiscard = async () => {
    const action = currentAction.value
    if (!action?.uuid) {
      close()
      return
    }

    try {
      await AgentActionsService.agentActionsRejectCreate({
        requestBody: { agent_action_uuid: action.uuid }
      })

      isAiPreloaded.value = false
      isEdited.value = false

      // Remove discarded action from queue
      pendingActions.value = pendingActions.value.filter((a) => a.uuid !== action.uuid)

      // Show feedback instead of closing immediately
      discardMode.value = true
      step.value = 'feedback'
    } catch (error) {
      console.error('Error discarding AI suggested action:', error)
      throw error // Let the page handle the toast
    }
  }

  /** Called when feedback is cancelled in discard mode — advance to next action or close */
  const handleDiscardFeedbackCancel = async () => {
    discardMode.value = false

    if (pendingActions.value.length > 0) {
      await advanceToNextAction()
      step.value = 'summary'
    } else {
      currentAction.value = null
      summaryParts.value = null
      close()
    }
  }

  const onAgentActivityFinished = () => {
    fetchActions()
  }

  const cleanup = () => {
    isOpen.value = false
    flowStrategies.clear()
  }

  return {
    // State
    isOpen,
    step,
    currentAction,
    isAiPreloaded,
    hasPendingActions,
    pendingActions,
    summaryParts,
    draftEdited,
    discardMode,

    // Registration
    registerActionFlow,

    // Actions
    markReviewed,
    markEdited,
    toggle,
    close,
    fetchActions,
    handleReview,
    handleSend,
    handleSuccessDismiss,
    handleDiscard,
    handleDiscardFeedbackCancel,
    onAgentActivityFinished,
    cleanup
  }
}
